import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Empty, Input, InputNumber, Skeleton } from 'antd';
import { DownloadOutlined, FileTextOutlined, FormOutlined } from '@ant-design/icons';

import { Button, PageTitle, Panel, Tag } from '../components/ds';
import { documentsApi, type DocTemplateField } from '../lib/wikiApi';
import './analytics/analytics.css';

/**
 * «Документы» (Q67-70, вид HUB 2.0): слева шаблоны — «Заполнить» открывает
 * форму прямо на странице (часть полей уже подставлена из CRM: ИНН, адрес,
 * дата), «Сформировать PDF»; справа — «Мои документы» со скачиванием.
 *
 * Маршрут: /documents
 */
export function DocumentsPage(): JSX.Element {
  const [fillId, setFillId] = useState<string | null>(null);
  const templatesQ = useQuery({ queryKey: ['wiki', 'documents', 'templates'], queryFn: () => documentsApi.listTemplates() });
  const templates = templatesQ.data ?? [];

  return (
    <div className="fk-adm-page">
      <PageTitle title="Документы" sub="Шаблоны заполняются данными из CRM: реквизиты партнёра подставятся сами" style={{ marginBottom: 0 }} />
      <div className="fk-adm-grid2">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
          <Panel title="Шаблоны" pad={0}>
            {templatesQ.isLoading ? (
              <div style={{ padding: 16 }}>
                <Skeleton active paragraph={{ rows: 4 }} />
              </div>
            ) : templates.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Шаблоны документов ещё не добавлены" />
            ) : (
              <div className="fk-adm-list">
                {templates.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className="fk-adm-list-row"
                    aria-pressed={fillId === t.id}
                    onClick={() => setFillId(t.id)}
                    style={{
                      gridTemplateColumns: '36px minmax(0,1fr) auto',
                      textAlign: 'left',
                      border: 'none',
                      font: 'inherit',
                      cursor: 'pointer',
                      background: fillId === t.id ? 'var(--fk-orange-soft)' : 'transparent',
                    }}
                  >
                    <span style={{ width: 36, height: 36, borderRadius: 6, background: 'var(--fk-info-soft)', color: 'var(--fk-info)', display: 'grid', placeItems: 'center', fontSize: 17 }}>
                      <FileTextOutlined />
                    </span>
                    <span style={{ minWidth: 0 }}>
                      <b className="fk-adm-ellipsis" style={{ display: 'block' }}>{t.name}</b>
                      <span className="fk-adm-note">Полей: {t.fieldsCount}</span>
                    </span>
                    <span style={{ color: 'var(--fk-orange)', fontWeight: 600, fontSize: 13, display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                      <FormOutlined /> Заполнить
                    </span>
                  </button>
                ))}
              </div>
            )}
          </Panel>
          {fillId && <FillPanel key={fillId} templateId={fillId} onClose={() => setFillId(null)} />}
        </div>
        <MyDocumentsSection />
      </div>
    </div>
  );
}

function MyDocumentsSection(): JSX.Element {
  const { data, isLoading, isError } = useQuery({ queryKey: ['wiki', 'documents', 'mine'], queryFn: () => documentsApi.listMine(), retry: false });
  if (isError) return <></>;
  return (
    <Panel title="Мои документы" pad={0}>
      {isLoading ? (
        <div style={{ padding: 16 }}>
          <Skeleton active paragraph={{ rows: 3 }} />
        </div>
      ) : (data?.length ?? 0) === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Пока нет сформированных документов" />
      ) : (
        <div className="fk-adm-list">
          {data!.map((d) => (
            <div key={d.id} className="fk-adm-list-row" style={{ gridTemplateColumns: '20px minmax(0,1fr) auto' }}>
              <FileTextOutlined style={{ color: 'var(--fk-info)', fontSize: 16 }} />
              <span style={{ minWidth: 0 }}>
                <b className="fk-adm-ellipsis" style={{ display: 'block' }}>{d.templateName}</b>
                <span className="fk-adm-note">
                  {d.isMine ? 'Вы' : d.authorName} ·{' '}
                  {new Date(d.createdAt).toLocaleString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  {!d.isMine && ' · коллега'}
                </span>
              </span>
              <Button size="sm" variant="ghost" icon={<DownloadOutlined />} href={documentsApi.pdfUrl(d.id)}>
                PDF
              </Button>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

function FillPanel({ templateId, onClose }: { templateId: string; onClose: () => void }): JSX.Element {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['wiki', 'documents', 'template', templateId],
    queryFn: () => documentsApi.getTemplate(templateId),
    retry: false,
  });
  const [values, setValues] = useState<Record<string, string>>({});
  // Префилл значениями из контекста (ИНН, адрес, дата) при загрузке шаблона.
  useEffect(() => {
    if (data) setValues({ ...data.defaults });
  }, [data]);

  const fillMutation = useMutation({
    mutationFn: () => documentsApi.fill(templateId, values),
    onSuccess: async (r) => {
      void message.success('Документ сформирован');
      await qc.invalidateQueries({ queryKey: ['wiki', 'documents', 'mine'] });
      window.open(documentsApi.pdfUrl(r.id), '_blank', 'noopener,noreferrer');
      onClose();
    },
    onError: (e: Error) => void message.error(e.message || 'Не удалось сформировать'),
  });
  const missingRequired = useMemo(() => !data || data.fields.some((f) => f.required && !values[f.key]?.trim()), [data, values]);

  return (
    <Panel
      title={data?.name ?? 'Заполнение документа'}
      right={
        <Button size="sm" variant="text" onClick={onClose}>
          Закрыть
        </Button>
      }
    >
      {isLoading ? (
        <Skeleton active paragraph={{ rows: 5 }} />
      ) : error ? (
        <Alert type="error" showIcon message="Не удалось загрузить шаблон" description={(error as Error).message} />
      ) : !data ? null : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {data.fields.length === 0 && <Alert type="info" showIcon message="У шаблона нет полей — документ сформируется как есть." />}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))', gap: 14 }}>
            {data.fields.map((f) => (
              <FieldInput key={f.key} field={f} value={values[f.key] ?? ''} autofilled={!!data.defaults[f.key]} onChange={(v) => setValues((s) => ({ ...s, [f.key]: v }))} />
            ))}
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button variant="primary" icon={<DownloadOutlined />} loading={fillMutation.isPending} disabled={missingRequired} onClick={() => fillMutation.mutate()}>
              Сформировать PDF
            </Button>
            <span className="fk-adm-note">Поля с пометкой «из CRM» заполнены автоматически — проверьте их</span>
          </div>
        </div>
      )}
    </Panel>
  );
}

function FieldInput({
  field,
  value,
  autofilled,
  onChange,
}: {
  field: DocTemplateField;
  value: string;
  autofilled: boolean;
  onChange: (v: string) => void;
}): JSX.Element {
  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, display: 'flex', gap: 6, alignItems: 'center' }}>
        {field.label}
        {field.required && <span style={{ color: 'var(--fk-orange)' }}>*</span>}
        {autofilled && <Tag color="blue">из CRM</Tag>}
      </div>
      {field.type === 'textarea' ? (
        <Input.TextArea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          autoSize={{ minRows: 2, maxRows: 6 }}
        />
      ) : field.type === 'number' ? (
        <InputNumber
          value={value ? Number(value) : undefined}
          onChange={(v) => onChange(v == null ? '' : String(v))}
          placeholder={field.placeholder}
          style={{ width: '100%' }}
        />
      ) : field.type === 'date' ? (
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder ?? 'дд.мм.гггг'}
        />
      ) : (
        <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} />
      )}
    </div>
  );
}
